"use client";

import { useEffect } from "react";
import { AuthProvider } from "@cede/types";
import { FEATURE_FLAGS } from "@cede/types";
import type { AuthStateProvider } from "./types";
import { useLocalAuth } from "./useLocalAuth";
import { useTonConnect, useSetupTon } from "./useTonConnect";
import { LocalWalletModal } from "@/components/shared/LocalWalletModal/LocalWalletModal";
import { BRAND_THEME } from "@/constants/theme";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { clientConfig } from "@/lib/config/client-config";
import { useActiveTheme } from "@/store/themeStore";
import {
  getIdentityToken,
  useIdentityToken as usePrivyIdentityToken,
  usePrivy,
  PrivyProvider,
} from "@privy-io/react-auth";
import { toSolanaWalletConnectors } from "@privy-io/react-auth/solana";
import { THEME, TonConnectUIProvider } from "@tonconnect/ui-react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

const THEME_LOGO: Record<string, string> = {
  [BRAND_THEME.DEFAULT]: "/favicon.svg",
  [BRAND_THEME.KRAKEN]: "/logos/favicon-kraken.svg",
  [BRAND_THEME.CRYPTOCOM]: "/logos/favicon-cryptocom.svg",
  [BRAND_THEME.COINBASE]: "/logos/favicon-coinbase.svg",
};

export interface AuthState extends AuthStateProvider {
  setAuthState: (state: Partial<AuthState>) => void;
}

const useAuthStore = create<AuthState>((set) => ({
  ready: false,
  authenticated: false,
  user: null,
  login: async () => {
    // eslint-disable-next-line no-console
    console.warn("Authentication provider not initialized");
  },
  logout: async () => {
    // eslint-disable-next-line no-console
    console.warn("Authentication provider not initialized");
  },
  identityProvider: "privy",
  getAccessToken: async () => null,
  identityToken: null,
  refreshIdentityToken: null,
  setAuthState: (state) => set((prev) => ({ ...prev, ...state })),
  initializeWidget: () => () => {
    // eslint-disable-next-line no-console
    console.warn("Authentication provider not needed to be initialized");
  },
}));

const AuthStateInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const privyAuthenticationContext = usePrivy();
  const tonAuthenticationContext = useTonConnect();
  const localAuthContext = useLocalAuth();
  const { identityToken: privyIdentityToken } = usePrivyIdentityToken();
  const setAuthState = useAuthStore((state) => state.setAuthState);
  const { isTMA } = useTelegramContext();

  // Feature flags
  const isTonEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.TON_CONNECT_HUB);
  const isLocalMode = clientConfig.nodeEnv === "local";

  // Initialize providers
  useSetupTon();

  useEffect(() => {
    // Local mode bypasses all auth providers
    if (isLocalMode) {
      setAuthState({
        ...localAuthContext,
        identityProvider: "local" as AuthProvider,
        refreshIdentityToken: null,
      });
      return;
    }

    // Determine active provider based on authentication state and feature flags
    let activeProvider: AuthProvider = "privy";
    let activeAuthState: Partial<AuthStateProvider> = privyAuthenticationContext;
    let activeIdentityToken = privyIdentityToken;
    let activeRefreshIdentityToken: AuthStateProvider["refreshIdentityToken"] = getIdentityToken;

    if (isTonEnabled && (tonAuthenticationContext.authenticated || isTMA || clientConfig.tmaDev)) {
      activeProvider = "ton";
      activeAuthState = tonAuthenticationContext;
      activeIdentityToken = tonAuthenticationContext.identityToken;
      activeRefreshIdentityToken = tonAuthenticationContext.refreshIdentityToken;
    }

    setAuthState({
      ...activeAuthState,
      identityToken: activeIdentityToken,
      identityProvider: activeProvider,
      refreshIdentityToken: activeRefreshIdentityToken,
    });

    // Auto-logout from other providers when one is active
    if (activeProvider === "ton" && privyAuthenticationContext.authenticated) {
      privyAuthenticationContext.logout();
    }
    if (activeProvider === "privy" && tonAuthenticationContext.authenticated) {
      tonAuthenticationContext.logout();
    }
  }, [
    // Local mode
    isLocalMode,
    localAuthContext.authenticated,
    localAuthContext.user,
    // Privy
    privyAuthenticationContext.user,
    privyAuthenticationContext.ready,
    privyAuthenticationContext.authenticated,
    privyIdentityToken,
    // TON Connect
    tonAuthenticationContext.user,
    tonAuthenticationContext.ready,
    tonAuthenticationContext.authenticated,
    // Feature flags
    isTonEnabled,
    setAuthState,
  ]);

  return (
    <>
      {isLocalMode && localAuthContext.showModal && (
        <LocalWalletModal
          open={localAuthContext.showModal}
          onSelect={localAuthContext.selectAddress}
          onClose={localAuthContext.closeModal}
        />
      )}
      {children}
    </>
  );
};

// Configure Solana wallet connectors for Phantom, Solflare, etc.
// shouldAutoConnect: false to avoid extension popups on page load
const solanaConnectors = toSolanaWalletConnectors({
  shouldAutoConnect: false,
});

export const AuthenticationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const activeTheme = useActiveTheme();
  const logo = THEME_LOGO[activeTheme] || THEME_LOGO[BRAND_THEME.DEFAULT];

  return (
    <TonConnectUIProvider
      manifestUrl={`${clientConfig.appUrl}/tonconnect-manifest.json`}
      uiPreferences={{
        theme: THEME.DARK,
        borderRadius: "s",
      }}
    >
      <PrivyProvider
        appId={clientConfig.privyAppId}
        config={{
          loginMethods: ["wallet"],
          appearance: {
            theme: "dark",
            accentColor: "#5EA6FF",
            logo,
            showWalletLoginFirst: true,
            walletChainType: "ethereum-only", // Keep ethereum-only for login, Solana is handled via linkWallet
            walletList: [
              "base_account",
              "rabby_wallet",
              "metamask",
              "detected_ethereum_wallets",
              "wallet_connect_qr",
              "wallet_connect",
            ],
            landingHeader: "Log in or sign up",
            loginMessage: "",
          },
          embeddedWallets: {
            ethereum: { createOnLogin: "off" },
            solana: { createOnLogin: "off" },
          },
          externalWallets: {
            walletConnect: { enabled: true },
            coinbaseWallet: undefined,
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AuthStateInitializer>{children}</AuthStateInitializer>
      </PrivyProvider>
    </TonConnectUIProvider>
  );
};

export const useAuthenticationProvider = () => {
  return useAuthStore(
    useShallow((state) => ({
      ready: state.ready,
      authenticated: state.authenticated,
      user: state.user,
      login: state.login,
      logout: state.logout,
      identityProvider: state.identityProvider,
      getAccessToken: state.getAccessToken,
      identityToken: state.identityToken,
      refreshIdentityToken: state.refreshIdentityToken,
      initializeWidget: state.initializeWidget,
    })),
  );
};
